import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import LocalModal from 'components/base/local-modal';
import React, { FC, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { formatCurrency, handleApiError } from 'utils/functions';
import AssignMealModal from '../assign-meal';
import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg';
import { ReactComponent as EditIcon } from 'assets/icons/local-edit-icon.svg';
import { ReactComponent as DollarIcon } from 'assets/icons/dollar-icon.svg';
import happyPeople from 'assets/images/happy-people.png';
import GroupOrderService from 'services/group-order.service';
import useOrdersContext from 'contexts/orders.context';

interface GroupOrderInfoModalFooterProps {
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}

interface GroupOrderInfoModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const GroupOrderInfoModalFooter: FC<GroupOrderInfoModalFooterProps> = ({
  onCancel,
  onConfirm,
  loading
}) => (
  <div className='flex justify-end'>
    <div className='flex gap-5'>
      <Button
        small
        color='primary'
        variant='outline'
        className='mx-auto max-w-[140px]'
        onClick={onCancel}>
        Cancel Order
      </Button>
      <Button
        small
        color='primary'
        variant='block'
        className='mx-auto max-w-[140px]'
        onClick={onConfirm}
        loading={loading}>
        Confirm
      </Button>
    </div>
  </div>
);

const GroupOrderInfoModal = (props: GroupOrderInfoModalProps) => {
  const { show, onClose, onConfirm } = props;
  const { members } = useDashboardContext();
  const { calculatedOrder } = useOrdersContext();

  const [loading, setLoading] = useState(false);

  const [showMembersModal, setShowMembersModal] = useState(false);
  const [membersIds, setMembersIds] = useState<string[]>([]);

  const inviteMembers = async () => {
    setLoading(true);
    try {
      const res = await apiService.post('order/add-members-to-company', {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data: {
          calculated_order_id: calculatedOrder.id,
          company_user_ids: membersIds
        }
      });
      if (res) {
        toast(<ToastMessage type='success' message='Invite has been sent' />);
        onClose();
      }
    } catch (err) {
      handleApiError(err, 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalModal
      isOpen={show}
      onRequestClose={onClose}
      footer={
        <GroupOrderInfoModalFooter
          onCancel={onConfirm}
          onConfirm={inviteMembers}
          loading={loading}
        />
      }>
      <div className='flex flex-col gap-14'>
        <div className='flex flex-col gap-6'>
          <article>
            <img src={happyPeople} alt='Happy people' className='w-[129px] h-[129px]' />
            <h3 className='text-xl text-[#2E3031] font-semibold'>Group order info</h3>
            <p className='text-sm text-[#6F7376]'>
              Invite your team to order with you, from their own phones
            </p>
          </article>
          <div className='flex flex-col gap-[18px]'>
            <div className='flex items-center gap-3'>
              <DollarIcon />
              <p className='text-sm text-[#6F7376]'>
                Spending limit?&nbsp;
                <span className='text-[#2E3031]'>
                  {calculatedOrder.group_spend_limit
                    ? formatCurrency(Number(calculatedOrder.group_spend_limit))
                    : 'No limit'}
                </span>
              </p>
            </div>
            <div className='flex items-center gap-3'>
              <PeopleIcon />
              <p className='text-sm text-[#6F7376]'>Invite members to join</p>
              <button className='ml-auto p-2 rounded-full cursor-pointer hover:bg-black/10'>
                <EditIcon onClick={() => setShowMembersModal(true)} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <AssignMealModal
        title={'Invite members'}
        show={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        handleClick={(members) => {
          setMembersIds(members.map((member) => member.user.id));
        }}
        defaultMembers={GroupOrderService.GetDefaultMembers(
          members,
          calculatedOrder?.company_user_ids || []
        )}
      />
    </LocalModal>
  );
};

export default GroupOrderInfoModal;
